import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Incident {
  id: string;
  org_id: string;
  title: string;
  priority: string;
  impact?: string;
  status: string;
  discovered_at: string;
  early_warning_sent: boolean;
  initial_report_sent: boolean;
  final_report_sent: boolean;
  early_report?: string;
  initial_72h_report?: string;
  final_30d_report?: string;
  actions?: string;
  lessons_learned?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

interface IncidentFilters {
  status?: string;
  priority?: string;
}

interface CreateIncidentData {
  title: string;
  priority?: string;
  impact?: string;
  status?: string;
  discovered_at?: string;
  early_warning_sent?: boolean;
  initial_report_sent?: boolean;
  final_report_sent?: boolean;
  early_report?: string;
  initial_72h_report?: string;
  final_30d_report?: string;
  actions?: string;
  lessons_learned?: string;
  org_id: string;
}

interface UpdateIncidentData {
  title?: string;
  priority?: string;
  impact?: string;
  status?: string;
  discovered_at?: string;
  early_warning_sent?: boolean;
  initial_report_sent?: boolean;
  final_report_sent?: boolean;
  early_report?: string;
  initial_72h_report?: string;
  final_30d_report?: string;
  actions?: string;
  lessons_learned?: string;
}

export const useIncidents = (orgId?: string, filters?: IncidentFilters) => {
  const queryClient = useQueryClient();

  const incidentsQuery = useQuery({
    queryKey: ['incidents', orgId, filters?.status, filters?.priority],
    queryFn: async () => {
      if (!orgId) return [];
      
      let query = supabase
        .from('incidents')
        .select('*')
        .eq('org_id', orgId);

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.priority && filters.priority !== 'all') {
        query = query.eq('priority', filters.priority);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map<Incident>((row) => ({
        id: row.id,
        org_id: row.org_id,
        title: row.title,
        priority: row.priority ?? 'medium',
        impact: row.impact ?? undefined,
        status: row.status ?? 'draft',
        discovered_at: row.discovered_at,
        early_warning_sent: row.early_warning_sent ?? false,
        initial_report_sent: row.initial_report_sent ?? false,
        final_report_sent: row.final_report_sent ?? false,
        early_report: row.early_report ?? undefined,
        initial_72h_report: row.initial_72h_report ?? undefined,
        final_30d_report: row.final_30d_report ?? undefined,
        actions: row.actions ?? undefined,
        lessons_learned: row.lessons_learned ?? undefined,
        created_at: row.created_at,
        updated_at: row.updated_at,
        created_by: row.created_by ?? undefined,
        updated_by: row.updated_by ?? undefined,
      }));
    },
    enabled: !!orgId,
  });

  const createIncidentMutation = useMutation({
    mutationFn: async (data: CreateIncidentData) => {
      const { error } = await supabase.from('incidents').insert([{
        org_id: data.org_id,
        title: data.title,
        priority: data.priority ?? 'medium',
        impact: data.impact ?? null,
        status: data.status ?? 'draft',
        discovered_at: data.discovered_at ?? new Date().toISOString(),
        early_warning_sent: data.early_warning_sent ?? false,
        initial_report_sent: data.initial_report_sent ?? false,
        final_report_sent: data.final_report_sent ?? false,
        early_report: data.early_report ?? null,
        initial_72h_report: data.initial_72h_report ?? null,
        final_30d_report: data.final_30d_report ?? null,
        actions: data.actions ?? null,
        lessons_learned: data.lessons_learned ?? null,
      }]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      toast.success('Incident created successfully');
    },
    onError: (error) => {
      console.error('Error creating incident:', error);
      toast.error('Failed to create incident');
    },
  });

  const updateIncidentMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateIncidentData }) => {
      const updateData: Record<string, any> = {};
      
      if (data.title !== undefined) updateData.title = data.title;
      if (data.priority !== undefined) updateData.priority = data.priority;
      if (data.impact !== undefined) updateData.impact = data.impact ?? null;
      if (data.status !== undefined) updateData.status = data.status;
      if (data.discovered_at !== undefined) updateData.discovered_at = data.discovered_at;
      if (data.early_warning_sent !== undefined) updateData.early_warning_sent = data.early_warning_sent;
      if (data.initial_report_sent !== undefined) updateData.initial_report_sent = data.initial_report_sent;
      if (data.final_report_sent !== undefined) updateData.final_report_sent = data.final_report_sent;
      if (data.early_report !== undefined) updateData.early_report = data.early_report ?? null;
      if (data.initial_72h_report !== undefined) updateData.initial_72h_report = data.initial_72h_report ?? null;
      if (data.final_30d_report !== undefined) updateData.final_30d_report = data.final_30d_report ?? null;
      if (data.actions !== undefined) updateData.actions = data.actions ?? null;
      if (data.lessons_learned !== undefined) updateData.lessons_learned = data.lessons_learned ?? null;

      const { error } = await supabase
        .from('incidents')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      toast.success('Incident updated successfully');
    },
    onError: (error) => {
      console.error('Error updating incident:', error);
      toast.error('Failed to update incident');
    },
  });

  const deleteIncidentMutation = useMutation({
    mutationFn: async (id: string) => {
      // First delete related links
      await supabase.from('links').delete().or(`source_id.eq.${id},target_id.eq.${id}`);
      
      // Then delete the incident
      const { error } = await supabase.from('incidents').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      toast.success('Incident deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting incident:', error);
      toast.error('Failed to delete incident');
    },
  });

  return {
    incidents: incidentsQuery.data,
    isLoading: incidentsQuery.isLoading,
    error: incidentsQuery.error,
    getIncidentById: (id: string) => incidentsQuery.data?.find(i => i.id === id),
    createIncident: createIncidentMutation.mutate,
    updateIncident: updateIncidentMutation.mutate,
    deleteIncident: deleteIncidentMutation.mutate,
    isCreating: createIncidentMutation.isPending,
    isUpdating: updateIncidentMutation.isPending,
    isDeleting: deleteIncidentMutation.isPending,
  };
};
